import React, { useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { StoreDetailPanel } from './StoreDetailPanel';
import { MapLegend } from './MapLegend';
import storesData from '../../data/stores.json';
// Status colors
const statusColors = {
  success: '#22c55e',
  warning: '#eab308',
  danger: '#ef4444',
  default: '#1C69D4'
};
// New Store interface matching the updated data structure
export interface Store {
  id: string;
  name: string;
  country: string;
  city: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  coords: {
    lat: number;
    lng: number;
  };
  // Optional legacy / enriched fields (e.g. from a ratings API)
  score?: number;
  status?: 'success' | 'warning' | 'danger';
  type?: string;
}
export const stores: Store[] = storesData as Store[];
// Pattern: 5 green, 2 yellow, 1 red (repeating cycle of 8)
const RATING_CYCLE = [{
  status: 'success' as const,
  score: 4.8
}, {
  status: 'success' as const,
  score: 4.6
}, {
  status: 'success' as const,
  score: 4.9
}, {
  status: 'success' as const,
  score: 4.5
}, {
  status: 'success' as const,
  score: 4.7
}, {
  status: 'warning' as const,
  score: 3.9
}, {
  status: 'warning' as const,
  score: 4.1
}, {
  status: 'danger' as const,
  score: 3.2
}];
function assignDynamicRating(index: number): {
  score: number;
  status: 'success' | 'warning' | 'danger';
} {
  return RATING_CYCLE[index % RATING_CYCLE.length];
}
// Enrich stores with dynamic ratings (only when score/status not already set in JSON)
const enrichedStores: Store[] = stores.map((store, index) => {
  if (store.score !== undefined && store.status !== undefined) return store;
  return {
    ...store,
    ...assignDynamicRating(index)
  };
});
// Derive marker color: use status if present, otherwise score-based, otherwise default blue
function getMarkerColor(store: Store): string {
  if (store.status) {
    return statusColors[store.status] ?? statusColors.default;
  }
  if (store.score !== undefined) {
    if (store.score >= 4.5) return statusColors.success;
    if (store.score >= 3.5) return statusColors.warning;
    return statusColors.danger;
  }
  return statusColors.default;
}
export function StoreMap() {
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const handleMarkerClick = (store: Store) => {
    setSelectedStore(store);
    setIsPanelOpen(true);
  };
  return <div className="relative w-full h-full bg-gray-100 overflow-hidden" data-id="element-176">
      <MapContainer center={[51.1657, 10.4515]} zoom={6.5} style={{
      height: '100%',
      width: '100%'
    }} zoomControl={true} data-id="element-177">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" data-id="element-178" />

        {enrichedStores.map(store => <CircleMarker key={store.id} center={[store.coords.lat, store.coords.lng]} radius={6} pathOptions={{
        fillColor: getMarkerColor(store),
        fillOpacity: 0.9,
        color: '#ffffff',
        weight: 3
      }} eventHandlers={{
        click: () => handleMarkerClick(store)
      }} data-id="element-179">
            <Popup data-id="element-180">
              <div className="text-sm" data-id="element-181">
                <strong data-id="element-182">{store.name}</strong>
                <br data-id="element-183" />
                {store.city}, {store.country}
                {store.score !== undefined && <>
                    <br data-id="element-184" />
                    Bewertung: {store.score} ⭐
                  </>}
              </div>
            </Popup>
          </CircleMarker>)}
      </MapContainer>

      <MapLegend data-id="element-185" />

      <StoreDetailPanel isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)} store={selectedStore} data-id="element-186" />
    </div>;
}